import { defaultUseQueryRefetchOptions } from '@/_shared/constants'
import { CreativeOSFeatures } from '@/_shared/subscriptionPlans'
import { trpc } from '@/utils/trpc'
import { ArrowRight } from '@mynaui/icons-react'
import { Link } from '@tanstack/react-router'
import AccessControl from './AccessControl'
import SearchDialogAdTemplateImage from './SearchDialogAdTemplateImage'
import SearchDialogEmailLanderTemplateImage from './SearchDialogEmailTemplateImage'
import { LockTagLargeNoBg, LockTagSmall } from './custom-components/LockTag'
import { ScrollArea, ScrollBar } from './shadcn/scroll-area'
import { Skeleton } from './shadcn/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from './shadcn/tooltip'

const SearchDialogNewTemplatesTab = () => {
  // Templates
  const { data: newAdTemplates, isLoading: adTemplatesIsLoading } = trpc.filterTemplatesByCollections.useQuery({
    sortingOptions: 'Recent',
    limit: 4,
    Ready: true,
    Tags: undefined,
    collectionIds: [],
  })

  const { data: newEmailTemplates, isLoading: emailTemplatesIsLoading } = trpc.emails.useQuery(
    {
      sortingOptions: 'Recent',
      limit: 4,
    },
    defaultUseQueryRefetchOptions,
  )

  const { data: newLandersTemplates, isLoading: landerTemplatesIsLoading } = trpc.landingPages.useQuery(
    {
      sortingOptions: 'Recent',
      limit: 4,
    },
    defaultUseQueryRefetchOptions,
  )

  const { data: brands, isLoading: brandsIsLoading } = trpc.getBrandsPaginatedV2.useQuery(
    {
      sort: [
        {
          field: 'createdAt',
          order: 'desc',
        },
      ],
      limit: 7,
    },
    defaultUseQueryRefetchOptions,
  )

  return (
    <div className="space-y-2.5 pr-5">
      <div className="space-y-2.5">
        <h6 className="text-sm">Ads</h6>
        <div className="">
          {adTemplatesIsLoading ? (
            <div className="grid grid-cols-4 gap-2.5">
              {[...Array(4)].map((_, index) => (
                <Skeleton key={index} className="w-full h-[7.25rem] rounded-2xl" />
              ))}
            </div>
          ) : (
            <ScrollArea className="whitespace-nowrap space-y-2.5">
              <div className="grid grid-cols-4 gap-2.5">
                {newAdTemplates?.ATRecords.map((item) => (
                  <SearchDialogAdTemplateImage
                    key={item['Creative ID']}
                    item={{
                      ...item,
                      form: '',
                      'Example Image': [],
                      'Template Image': [],
                      Status: '',
                      AdDbId: null,
                      Expert: undefined,
                      Tags: undefined,
                      Liked: item.Liked ?? false,
                      'Canva Template LInk': item['Canva Template LInk'] ?? '',
                    }}
                    isEqualWidth
                  />
                ))}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          )}
        </div>
        <div className="flex justify-end mr-3">
          <Link className="text-slate-500" to="/feeds/inspiration" search={{ orderFilter: 'Recent' }}>
            <span className="flex items-center gap-2 text-sm">
              <span>See all</span> <ArrowRight size={'1rem'} />
            </span>
          </Link>
        </div>
      </div>
      <div className="space-y-2.5">
        <h6 className="text-sm">Emails</h6>
        <div className="">
          {emailTemplatesIsLoading ? (
            <div className="grid grid-cols-4 gap-2.5">
              {[...Array(4)].map((_, index) => (
                <Skeleton key={index} className="w-full h-[7.25rem] rounded-2xl" />
              ))}
            </div>
          ) : (
            <ScrollArea className="whitespace-nowrap space-y-2.5">
              <div className="grid grid-cols-4 gap-2.5">
                {newEmailTemplates?.Emails.map((item) => (
                  <SearchDialogEmailLanderTemplateImage
                    key={item['Email ID']}
                    item={{
                      Screenshot: item['Email Screenshot'],
                      ID: item['Email ID'].toString(),
                      created: item.Created,
                      category: item.Category ?? '',
                      isSaved: item.isSaved,
                      imageUrl: item.imageUrl,
                      brandId: item.brandId,
                      brandName: item.brandName,
                      brandImage: item.brandImage,
                    }}
                    type="email"
                    isEqualWidth
                  />
                ))}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          )}
        </div>
        <div className="flex justify-end mr-3">
          <Link className="text-slate-500" to="/feeds/inspiration/emails" search={{ orderFilter: 'Newest' }}>
            <span className="flex items-center gap-2 text-sm">
              <span>See all</span> <ArrowRight size={'1rem'} />
            </span>
          </Link>
        </div>
      </div>
      {(brands?.results ?? []).length > 0 ? (
        <div className="space-y-1.5 hidden lg:block">
          <div className="flex justify-between pr-5">
            <h6 className="text-gray-500 font-medium text-sm">Brands</h6>
          </div>
          {brandsIsLoading ? (
            <div className="flex w-max space-x-2.5 p-4">
              {[...Array(7)].map((_, index) => (
                <Skeleton key={index} className="w-16 h-16 rounded-full" />
              ))}
            </div>
          ) : (
            <ScrollArea className="whitespace-nowrap space-y-2.5">
              <div className="flex w-max space-x-2.5 px-4 pb-4 mt-2">
                {brands?.results.slice(0, 7).map((item) => (
                  <Tooltip>
                    <TooltipTrigger>
                      <AccessControl
                        feature={CreativeOSFeatures.SHOW_SEARCH_BRANDS_BUTTON}
                        FeatureUnlocked={
                          <Link to={`/feeds/brands/$brandID`} params={{ brandID: item.brandId }} key={item.brandId}>
                            <img
                              src={item.brandImage ?? undefined}
                              className="w-14 h-14 bg-slate-100 rounded-full object-cover hvr-float"
                              key={item.brandId}
                              alt={item.brandName}
                            />
                          </Link>
                        }
                        FeatureLocked={
                          <div className="relative">
                            <img
                              src={item.brandImage ?? undefined}
                              className="w-14 h-14 bg-slate-100 rounded-full object-cover blur-sm"
                              key={item.brandId}
                              alt={item.brandName}
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="bg-black bg-opacity-50 rounded-full p-0.5">
                                <LockTagLargeNoBg />
                              </div>
                            </div>
                          </div>
                        }
                      />
                      <TooltipContent className="max-w-sm">
                        <p>{item.brandName}</p>
                      </TooltipContent>
                    </TooltipTrigger>
                  </Tooltip>
                ))}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          )}

          <div className="flex w-full justify-end pr-5">
            <AccessControl
              feature={CreativeOSFeatures.SHOW_SEARCH_BRANDS_BUTTON}
              FeatureUnlocked={
                <Link className="text-slate-500" to="/feeds/brands">
                  <span className="flex items-center gap-2 text-sm">
                    <span>See all</span> <ArrowRight size={'1rem'} />
                  </span>
                </Link>
              }
              FeatureLocked={
                <span className="flex items-center gap-2 text-sm opacity-50">
                  <span>See all</span> <LockTagSmall />
                </span>
              }
            />
          </div>
        </div>
      ) : null}
      <div className="space-y-2.5">
        <h6 className="text-sm">Landers</h6>
        <div className="">
          {landerTemplatesIsLoading ? (
            <div className="grid grid-cols-4 gap-2.5">
              {[...Array(4)].map((_, index) => (
                <Skeleton key={index} className="w-full h-[7.25rem] rounded-2xl" />
              ))}
            </div>
          ) : (
            <ScrollArea className="whitespace-nowrap space-y-2.5">
              <div className="grid grid-cols-4 gap-2.5">
                {newLandersTemplates?.LandingPages.map((item) => (
                  <SearchDialogEmailLanderTemplateImage
                    key={item['LP ID']}
                    item={{
                      Screenshot: item['Landing Page Screenshot'],
                      ID: item['LP ID'].toString(),
                      created: item.Created,
                      category: item.Category ?? '',
                      isSaved: item.isSaved,
                      imageUrl: item.imageUrl,
                      brandId: item.brandId,
                      brandName: item.brandName,
                      brandImage: item.brandImage,
                    }}
                    type="landing-page"
                    isEqualWidth
                  />
                ))}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          )}
        </div>
        <div className="flex justify-end mr-3">
          <Link className="text-slate-500" to="/feeds/inspiration/landing-pages" search={{ orderFilter: 'Newest' }}>
            <span className="flex items-center gap-2 text-sm">
              <span>See all</span> <ArrowRight size={'1rem'} />
            </span>
          </Link>
        </div>
      </div>
    </div>
  )
}

export default SearchDialogNewTemplatesTab
